import React, { useCallback, useRef } from "react";
import { Configure, InstantSearch, Hits, useSearchBox, useInstantSearch } from "react-instantsearch";
import AlgoliaPagination from "./AlgoliaPagination";
import {
  createQueryPageRouting,
  decodeEntities,
  trimExcerpt,
  useAlgoliaClient,
  useSearchInput,
} from "./utils";

const HitCard = ({ hit }) => {
  const title = decodeEntities(
    hit?.headline || hit?.title || hit?.pageHeadline || "Untitled"
  );
  const url = hit?.url || "#";
  const excerpt =
    hit?.excerpt ||
    hit?.pageIntroText ||
    hit?.introText ||
    hit?.contentBuilderContent ||
    "";
  const imageUrl =
    hit?.imageUrl ||
    "https://ayersbasementsystems.nyc3.digitaloceanspaces.com/assets/press-placeholder.png";
  const imageAlt = hit?.imageAltText || title || "Press placeholder";
  const servicesTitles = hit?.servicesTitles || [];
  const servicesSlugs = hit?.servicesSlugs || [];
  const servicesUrls = hit?.servicesUrls || [];
  const services = (servicesTitles.length ? servicesTitles : servicesSlugs).map(
    (label, idx) => ({
      label,
      url:
        servicesUrls[idx] ||
        (servicesSlugs[idx] ? `/services/${servicesSlugs[idx]}` : "#"),
    })
  );
  const postDate = hit?.postDate
    ? new Date(hit.postDate).toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      })
    : null;

  return (
    <article className="b-searchable-press-list__hit">
      {imageUrl ? (
        <a
          className="b-searchable-press-list__hit-image"
          href={url}
          title={title}
        >
          <img src={imageUrl} alt={imageAlt} loading="lazy" />
        </a>
      ) : null}
      <div className="b-searchable-press-list__hit-body">
        {postDate ? (
          <div className="b-searchable-press-list__hit-date">{postDate}</div>
        ) : null}
        {services.length ? (
          <div className="c-services-tags">
            {services.map((service) => (
              <a
                className="c-services-tags__tag"
                key={service.label}
                href={service.url}
                title={service.label}
              >
                {service.label}
              </a>
            ))}
          </div>
        ) : null}
        <h3>
          <a href={url} title={title}>
            {title}
          </a>
        </h3>
        {excerpt ? <p>{trimExcerpt(excerpt, 320)}</p> : null}
      </div>
    </article>
  );
};

const CustomSearchBox = ({ onScroll }) => {
  const { value, onChange, onReset, onSubmit } = useSearchInput({
    onChange: () => onScroll?.(),
    onSubmit: () => onScroll?.(),
    onReset: () => onScroll?.(),
  });

  return (
    <div className="ais-SearchBox">
      <form
        className="ais-SearchBox-form"
        role="search"
        noValidate
        onSubmit={onSubmit}
        onReset={onReset}
      >
        <input
          className="ais-SearchBox-input"
          type="search"
          value={value}
          onChange={onChange}
          placeholder="Ex. waterproofing, basement, etc…"
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck="false"
        />
        {value ? (
          <button
            type="reset"
            className="ais-SearchBox-reset"
            aria-label="Clear search"
          >
            ✕
          </button>
        ) : null}
        <button
          className="ais-SearchBox-submit"
          type="submit"
          aria-label="Submit search"
        >
          Search
        </button>
      </form>
    </div>
  );
};


const NoResults = ({ onScroll }) => {
  const { results, setIndexUiState } = useInstantSearch();
  const { refine: setQuery } = useSearchBox();

  const hasResults = results && results.nbHits > 0;

  const clearAll = () => {
    setQuery("");
    setIndexUiState((ui) => ({
      ...ui,
      query: "",
      page: 1,
    }));
    onScroll?.();
  };

  if (hasResults || !results) {
    return null;
  }

  return (
    <div className="b-searchable-press-list__no-results">
      <p>
        No articles match your filters.{" "}
        <button
          type="button"
          className="b-searchable-press-list__clear-link"
          onClick={clearAll}
        >
          Clear filters
        </button>
      </p>
    </div>
  );
};

export default function SearchablePressList({
  appId: propAppId,
  searchKey: propSearchKey,
  indexName: propIndexName,
  hitsPerPage = 6,
} = {}) {
  const { client: searchClient, appId, searchKey } = useAlgoliaClient(
    propAppId,
    propSearchKey
  );
  const indexName = propIndexName ?? "press";

  const containerRef = useRef(null);
  const anchorRef = useRef(null);

  const scrollToGrid = useCallback(() => {
    if (typeof window === "undefined") return;
    const el = anchorRef.current || containerRef.current;
    if (!el) return;
    window.requestAnimationFrame(() => {
      const top = el.getBoundingClientRect().top + window.pageYOffset;
      window.scrollTo({
        top,
        behavior: "smooth",
      });
    });
  }, []);

  if (!appId || !searchKey || !indexName || !searchClient) {
    return null;
  }

  return (
    <InstantSearch
      searchClient={searchClient}
      indexName={indexName}
      routing={createQueryPageRouting({ indexName })}
      future={{
        preserveSharedStateOnUnmount: true,
      }}
    >
      <Configure hitsPerPage={hitsPerPage} />
      <div className="b-searchable-press-list__inner" ref={containerRef}>
        <div
          className="b-searchable-press-list__anchor"
          ref={anchorRef}
          aria-hidden="true"
        />
        <div className="b-searchable-press-list__header">
          <div className="b-searchable-press-list__search">
            <span>Search all press</span>
            <CustomSearchBox onScroll={scrollToGrid} />
          </div>
        </div>

        <NoResults onScroll={scrollToGrid} />

        <div className="b-searchable-press-list__results">
          <div className="b-searchable-press-list__hits">
            <Hits hitComponent={HitCard} />
          </div>

          <AlgoliaPagination
            onScroll={scrollToGrid}
          />
        </div>
      </div>
    </InstantSearch>
  );
}
